import React, { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/loanTypesSteps";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import SubTitle from "app/components/common/typography/SubTitle";
import EmploymentDynamicForm from "app/components/Individual/applicant/employment/EmploymentDynamicForm";

import { EmploymentDetailsForm } from "app/utils/applicant/types/types";

const EmploymentDetails: React.FC = () => {
  const { applicants, ...remaingData } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );

  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
    clearErrors,
  } = useForm<EmploymentDetailsForm>({
    defaultValues: {
      incomeFrequency: [
        { incomeType: "gross", amount: 0, frequency: "annually" },
        { incomeType: "other", amount: 0, frequency: "annually" },
      ],
    },
    // shouldUnregister: true,
  });

  const employmenttype = watch("employmentType");

  const onSubmit = (data: EmploymentDetailsForm) => {
    const { employerName, employmentType, incomeFrequency } = data;

    const updatedIncomeFrequency = (incomeFrequency || []).map(
      (item, index) => ({
        ...item,
        incomeType:
          index === 0 ? "gross" : index === 1 ? "other" : item.incomeType || "",
      })
    );

    const validIncome = updatedIncomeFrequency
      .filter(
        (item) =>
          item.amount !== undefined &&
          !isNaN(Number(item.amount)) &&
          item.frequency?.trim()
      )
      .map((item) => {
        const existingIncome = applicants?.incomes?.find(
          (i) => i.type === item.incomeType
        );

        // ✅ add `id` only if it exists
        const base = {
          type: item.incomeType,
          amount: Number(item.amount),
          frequency: item.frequency,
        };

        return existingIncome?.id ? { ...base, id: existingIncome.id } : base;
      });
    const existingIncomes = applicants?.incomes || [];
    const mergedIncomes = [
      ...validIncome,
      ...existingIncomes.filter(
        (old) => !validIncome.some((newItem) => newItem.type === old.type)
      ),
    ];

    dispatch(
      addStepsData({
        ...remaingData,
        applicants: {
          ...applicants,
          employment: {
            ...applicants?.employment,
            employerName: employerName || "",
            employmentType,
          },
          incomes: mergedIncomes,
        },
      })
    );
    dispatch(nextStep());
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "incomeFrequency",
  });

  useEffect(() => {
    if (applicants?.employment) {
      reset({
        employerName: applicants.employment.employerName || "",
        employmentType: applicants.employment.employmentType || "",
        incomeFrequency: applicants.incomes?.map((item) => ({
          incomeType: item.type,
          amount: Number(item.amount),
          frequency: item?.frequency ?? "",
        })) || [
          { incomeType: "gross", amount: 0, frequency: "annually" },
          { incomeType: "other", amount: 0, frequency: "annually" },
        ],
      });
    }
  }, [applicants, reset]);

  useEffect(() => {
    if (employmenttype === "unemployed_or_retired") {
      clearErrors("employerName");
      clearErrors("incomeFrequency");
      fields.forEach((field, index) => {
        setValue(`incomeFrequency.${index}.amount`, 0);
      });
    } else if (
      employmenttype === "contractor" ||
      employmenttype === "self-employed"
    ) {
      clearErrors("employerName");
      clearErrors("incomeFrequency");
    }
  }, [employmenttype]);

  return (
    <>
      <ProgressBar progress={80} />
      <div className="w-full  sm:w-[70%] xl:w-[40%]  mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Employment Details" />
        <SubTitle title="Please enter your employment details below" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <EmploymentDynamicForm
            control={control}
            errors={errors}
            fields={fields}
            employmentTypeName="employmentType"
            employerName="employerName"
            incomeName="incomeFrequency"
            employmenttypeValue={watch("employmentType")}
          />
          {/* {fields.length < 3 && (
            <div className="flex items-center justify-end mt-6">
              <AddMore
                onClick={() =>
                  append({ incomeType: "", amount: 0, frequency: "" })
                }
                disabled={fields.length >= 3}
              />
            </div>
          )} */}
          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit">Next</ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default EmploymentDetails;
